import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, filter, finalize, map, mergeMap, tap, exhaustMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { ProfessorService } from 'src/app/services/professor.service';
import * as fromProfessorAction from '../actions/professor.action';
import { setLoadingSpinner } from '../actions/shared.action';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { ROUTER_NAVIGATION, RouterNavigatedAction } from '@ngrx/router-store';
import { APIResponse } from 'src/app/models/api-response.model';
import { Router } from '@angular/router';
import { ModalService } from 'src/app/services/modal.service';

@Injectable()
export class ProfessorEffects {

  getProfessors$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/professor');
      }),
      mergeMap(() => {
        return this.professorService.getProfessors().pipe(
          map((response: APIResponse) => {
            return fromProfessorAction.getProfessorsSuccess({ data: response?.details});
          })
        );
      })
    );
  });

  getProfessorSubjects$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromProfessorAction.getProfessorSubjects),
      exhaustMap((action) => 
        this.professorService.getProfessorSubjects().pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));

            return fromProfessorAction.getProfessorSubjectsSuccess({data: response?.details})
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromProfessorAction.getProfessorSubjectsFailed({error : response?.error?.message}))
          })
        )
      )
    )
  );

  getWorkItemStudentsGrade$ = createEffect(
    () => this.actions$.pipe(
      ofType(fromProfessorAction.getWorkItemStudentsGrade),
      exhaustMap((action) => 
        this.professorService.getWorkItemStudentsGrade(
          action?.subjectId,
          action?.componentId,
          action?.categoryId,
          action?.workItemId
        ).pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));

            return fromProfessorAction.getWorkItemStudentsGradeSuccess({data: response?.details})
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromProfessorAction.getWorkItemStudentsGradeFailed({error : response?.error?.message}))
          })
        )
      )
    )
  );

  encodeWorkItemStudentsGrade$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromProfessorAction.encodeWorkItemStudentsGrade),
      exhaustMap((action) => 
        this.professorService.encodeWorkItemStudentsGrade(action?.payload, action?.subjectGradeId).pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));

            this.modalService.successModal({message:'Grades Submitted Successfully.'});

            return fromProfessorAction.encodeWorkItemStudentsGradeSuccess({message: response?.message})
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));

            this.modalService.errorModal({message:'Request Failed. Check you form and try again.'});

            return of(fromProfessorAction.encodeWorkItemStudentsGradeFailed({error : response?.error?.message}))
          })
        )
      )
    )
  );

  getProfessorSubjectWorkItems$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromProfessorAction.getProfSubjectWorkItems),
      exhaustMap((action) => 
        this.professorService.getProfSubjectWorkItems(action?.payload, action?.subjectId).pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));

            return fromProfessorAction.getProfSubjectWorkItemsSuccess({data: response?.details})
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromProfessorAction.getProfSubjectWorkItemsFailed({message : response?.error?.message}))
          })
        )
      )
    )
  );

  addProfessorSubjectItem$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromProfessorAction.addProfessorSubjectItem),
      exhaustMap((action) => 
        this.professorService.addProfessorSubjectWorkItem(action?.payload, action?.subjectId).pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));

            this.modalService.successModal({message: 'Work Item Added Successfully.'})

            return fromProfessorAction.addProfessorSubjectItemSuccess({data: response?.details, message: response?.message})
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromProfessorAction.addProfessorSubjectItemFailed({message : response?.error?.message}))
          })
        )
      )
    )
  );

  updateProfessorSubjectItem$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromProfessorAction.updateProfessorSubjectItem),
      exhaustMap((action) => 
        this.professorService.updateProfessorSubjectWorkItem(action?.payload, action?.subjectId, action?.id).pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));

            this.modalService.successModal({message: 'Work Item Updated Successfully.'});

            return fromProfessorAction.updateProfessorSubjectItemSuccess({data: response?.details, message: response?.message})
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromProfessorAction.updateProfessorSubjectItemFailed({message : response?.error?.message}))
          })
        )
      )
    )
  );
  
  getProfSubjectStudents$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(ROUTER_NAVIGATION),
      filter((r : RouterNavigatedAction) => {
        return r.payload.routerState.url.startsWith('/home/students');
      }),
      mergeMap(() => {
        const subjectId = this.router.getCurrentNavigation()?.extras.state?.['subject_id'];
      
        return this.professorService.getProfSubjectStudents(subjectId).pipe(
          map((response: APIResponse) => {
            return fromProfessorAction.getProfSubjectStudentsSuccess({ data: response?.details});
          })
        );
      })
    );
  });
  
  geGradeDetails$ = createEffect(
    ()=> this.actions$.pipe(
      ofType(fromProfessorAction.getGradeDetails),
      exhaustMap((action) => 
        this.professorService.getGradeDetailsReport(action?.payload, action?.subjectId).pipe(
          map((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));

            return fromProfessorAction.getGradeDetailsSuccess({data: response?.details})
          }),
          catchError((response)=>{
            this.store.dispatch(setLoadingSpinner({status : false}));
            return of(fromProfessorAction.getGradeDetailsFailed({error : response?.error?.message}))
          })
        )
      )
    )
  );

  constructor(
    private professorService: ProfessorService,
    private modalService: ModalService,
    private store: Store<AppState>,
    private actions$: Actions,
    private router: Router
  ) {}
}